import { SealOfBiliteracyFormViewModel } from 'src/types/SealOfBiliteracyFormViewModel';
import { StudentModel } from 'src/types/StudentModel';
import { ApiResponseModel } from '../types/ApiResponseModel';
import { AxiosProgressEvent } from '../types/AxiosProgressEvent';
import { DataRequestConfig } from '../types/DataRequestConfig';
import { SealOfBiliteracyContactModel } from '../types/SealOfBiliteracyContactModel';
import { SealOfBiliteracyFileListItemModel } from '../types/SealOfBiliteracyFileListItemModel';
import { SealOfBiliteracyListItemModel } from '../types/SealOfBiliteracyListItemModel';
import { SealOfBiliteracyModel } from '../types/SealOfBiliteracyModel';
import { SelectOption } from '../types/SelectOption';
import { getApiBaseUrl } from './app.service';
import { executeApiRequest, getAuthToken } from './base.service';
import { getHttpClient, HttpClient } from './httpClient';

export const sealOfBiliteracyApiPaths = {
  getListItems: (schoolYear: string) =>
    `sealofbiliteracy?calendarYear=${schoolYear}`,
  getById: (id: string): string => `sealofbiliteracy/${id}`,
  create: 'sealofbiliteracy',
  update: 'sealofbiliteracy',
  getStudentByMSISId: (msisId: string, isSealOfBiliteracy: boolean,
    schoolYear?: string): string => {
    let url = `common/student/${msisId}?isSealOfBiliteracy=${isSealOfBiliteracy}`;
    if (schoolYear) {
      url += `&schoolYear=${encodeURIComponent(schoolYear)}`;
    }
    return url;
  },
  getContact: 'sealofbiliteracycontact',
  createContact: 'sealofbiliteracycontact',
  updateContact: 'sealofbiliteracycontact',
  getSchoolYears:
    'common/schoolyearsbymodule/formview/SealOfBiliteracy?showLongYears=true',
  deleteById: (id: string): string => `sealofbiliteracy/delete/${id}`,
  getForm: 'sealofbiliteracy/form',
  uploadFile: (schoolYear: string, studentIdentifier: string,
    leaIdentifier: string): string =>
    `file/sealofbiliteracy?schoolYear=${encodeURIComponent(
      schoolYear
    )}&studentIdentifier=${encodeURIComponent(studentIdentifier)}
    &leaIdentifier=${encodeURIComponent(leaIdentifier)}`,
  deleteFile: (leaId: string, id: string): string =>
    `file/sealofbiliteracy/${leaId}/${id}`,
  getAllFiles: (leaId: string): string => `file/sealofbiliteracy/${leaId}`,
  downloadFile: (leaId: string, metaId: string): string =>
    `file/sealofbiliteracy/download/${leaId}/${metaId}`,
  getFilesByStudent: (
    schoolYear: string,
    leaId: string,
    msisId: string
  ): string => `file/sealofbiliteracy/${schoolYear}/${leaId}/${msisId}`,
};

export const SealOfBiliteracyServiceClient = (
  config?: DataRequestConfig
): HttpClient =>
  getHttpClient(getApiBaseUrl(), getAuthToken() || '', config?.signal);

export const getSealOfBiliteracyListItems = async (
  schoolYear: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SealOfBiliteracyListItemModel[]>> => {
  return executeApiRequest<SealOfBiliteracyListItemModel[]>(async () => {
    return await SealOfBiliteracyServiceClient(config).get(
      sealOfBiliteracyApiPaths.getListItems(schoolYear)
    );
  });
};

export const getSealOfBiliteracyById = async (
  id: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SealOfBiliteracyModel>> => {
  return executeApiRequest<SealOfBiliteracyModel>(async () => {
    return await SealOfBiliteracyServiceClient(config).get(
      sealOfBiliteracyApiPaths.getById(id)
    );
  });
};

export const getStudentByMSISId = async (
  msisId: string,
  schoolYear: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<StudentModel>> => {
  return executeApiRequest<StudentModel>(async () => {
    return await SealOfBiliteracyServiceClient(config).get(
      sealOfBiliteracyApiPaths.getStudentByMSISId(msisId, true, schoolYear)
    );
  });
};

export const getSealOfBiliteracyContact = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SealOfBiliteracyContactModel>> => {
  return executeApiRequest<SealOfBiliteracyContactModel>(async () => {
    return await SealOfBiliteracyServiceClient(config).get(
      sealOfBiliteracyApiPaths.getContact
    );
  });
};

export const postSealOfBiliteracyContact = async (
  contact: SealOfBiliteracyContactModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SealOfBiliteracyContactModel>> => {
  return executeApiRequest<SealOfBiliteracyContactModel>(async () => {
    return await SealOfBiliteracyServiceClient(config).post(
      sealOfBiliteracyApiPaths.createContact,
      contact
    );
  });
};

export const putSealOfBiliteracyContact = async (
  contact: SealOfBiliteracyContactModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SealOfBiliteracyContactModel>> => {
  return executeApiRequest<SealOfBiliteracyContactModel>(async () => {
    return await SealOfBiliteracyServiceClient(config).put(
      sealOfBiliteracyApiPaths.updateContact,
      contact
    );
  });
};

export const getSealOfBiliteracySchoolYears = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await SealOfBiliteracyServiceClient(config).get(
      sealOfBiliteracyApiPaths.getSchoolYears
    );
  });
};

export const deleteSealOfBiliteracyById = async (
  id: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SealOfBiliteracyModel>> => {
  return executeApiRequest<SealOfBiliteracyModel>(async () => {
    return await SealOfBiliteracyServiceClient(config).delete(
      sealOfBiliteracyApiPaths.deleteById(id)
    );
  });
};

export const getSealOfBiliteracyFormViewModel = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SealOfBiliteracyFormViewModel>> => {
  return executeApiRequest<SealOfBiliteracyFormViewModel>(async () => {
    return await SealOfBiliteracyServiceClient(config).get(
      sealOfBiliteracyApiPaths.getForm
    );
  });
};

export const postSealOfBiliteracy = async (
  sealOfBiliteracy: SealOfBiliteracyModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SealOfBiliteracyModel>> => {
  return executeApiRequest<SealOfBiliteracyModel>(async () => {
    return await SealOfBiliteracyServiceClient(config).post(
      sealOfBiliteracyApiPaths.create,
      sealOfBiliteracy
    );
  });
};

export const putSealOfBiliteracy = async (
  sealOfBiliteracy: SealOfBiliteracyModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SealOfBiliteracyModel>> => {
  return executeApiRequest<SealOfBiliteracyModel>(async () => {
    return await SealOfBiliteracyServiceClient(config).put(
      sealOfBiliteracyApiPaths.update,
      sealOfBiliteracy
    );
  });
};

export const uploadSealOfBiliteracyFile = async (
  schoolYear: string,
  studentIdentifier: string,
  leaIdentifier: string,
  file: File,
  config?: DataRequestConfig & {
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  }
): Promise<ApiResponseModel<{ id: string }>> => {
  const formData = new FormData();
  formData.append('file', file);

  return executeApiRequest<{ id: string }>(async () => {
    return await SealOfBiliteracyServiceClient(config).upload(
      sealOfBiliteracyApiPaths.uploadFile(schoolYear, studentIdentifier, leaIdentifier),
      formData,
      config?.onUploadProgress
    );
  });
};

export const deleteSealOfBiliteracyFile = async (
  leaId: string,
  id: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<void>> => {
  return executeApiRequest<void>(async () => {
    return await SealOfBiliteracyServiceClient(config).delete(
      sealOfBiliteracyApiPaths.deleteFile(leaId, id)
    );
  });
};

export const getAllSealOfBiliteracyFiles = async (
  leaId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SealOfBiliteracyFileListItemModel[]>> => {
  return executeApiRequest<SealOfBiliteracyFileListItemModel[]>(async () => {
    return await SealOfBiliteracyServiceClient(config).get(
      sealOfBiliteracyApiPaths.getAllFiles(leaId)
    );
  });
};

export const downloadSealOfBiliteracyFile = async (
  leaId: string,
  metaId: string,
  config?: DataRequestConfig
): Promise<Blob> => {
  const response = await SealOfBiliteracyServiceClient(config).getFile<Blob>(
    sealOfBiliteracyApiPaths.downloadFile(leaId, metaId)
  );
  return response.data;
};

export const getSealOfBiliteracyFilesByStudent = async (
  schoolYear: string,
  leaId: string,
  msisId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SealOfBiliteracyFileListItemModel[]>> => {
  return executeApiRequest<SealOfBiliteracyFileListItemModel[]>(async () => {
    return await SealOfBiliteracyServiceClient(config).get(
      sealOfBiliteracyApiPaths.getFilesByStudent(schoolYear, leaId, msisId)
    );
  });
};
